import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';
import { WATER_SAMPLES } from './waterChemistryQuality';
import {
  DEFAULT_QUALITY_EXPLORE,
  QUALITY_CHALLENGES,
  QUALITY_EXPLORE_FIELDS,
  QUALITY_QUESTION,
  calculateQualityExplore,
  type QualityExploreSetup,
} from './waterChemistryExplore';
import { GuidelineMeter } from './WaterChemistryVisuals';

/** Reviewed samples with Ca²⁺ and Mg²⁺ values become shortcuts that fill the inputs. */
const PRESETS = WATER_SAMPLES.filter((sample) => sample.calcium !== undefined && sample.magnesium !== undefined).map((sample) => ({
  id: sample.id,
  label: sample.name,
  setup: {
    calciumMgL: sample.calcium ?? 0,
    magnesiumMgL: sample.magnesium ?? 0,
    dissolvedOxygenMgL: sample.dissolvedOxygen,
    temperatureC: sample.temperatureC,
  } satisfies QualityExploreSetup,
}));

/**
 * Explore view of water hardness and dissolved oxygen from the learner's own results. The learning
 * question and tasks sit directly above the inputs (CT-INTERACT C7); the readout and working sit
 * beside them.
 */
export function WaterChemistryQualityExplore() {
  const [setup, setSetup] = useState<QualityExploreSetup>(DEFAULT_QUALITY_EXPLORE);
  const result = useMemo(() => calculateQualityExplore(setup), [setup]);
  const challengeInput = useMemo(() => ({ setup, result }), [setup, result]);

  return (
    <section
      aria-label="Explore water hardness"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <p className="max-w-2xl text-sm font-medium leading-6 text-slate2">
        Enter your own test results. The model converts each ion to the mass of CaCO₃ with the same number of moles, adds
        them, and compares the total with the ADWG aesthetic value.
      </p>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <ModelInputPanel
            legend="Your water test results"
            fields={QUALITY_EXPLORE_FIELDS}
            values={setup}
            onChange={(next) => setSetup(next as QualityExploreSetup)}
            sliderKey="calciumMgL"
          />
          <div className="flex flex-wrap gap-2" role="group" aria-label="Sample presets">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setSetup(preset.setup)}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <ExploreChallenges question={QUALITY_QUESTION} challenges={QUALITY_CHALLENGES} result={challengeInput} />
        </div>
        <div className="min-w-0 space-y-4">
          <ul className="grid gap-2" aria-label="Hardness guideline meter">
            <GuidelineMeter parameter="hardness" value={result.hardness} />
          </ul>
          <dl className="grid grid-cols-2 gap-3" aria-label="Model readout">
            <div className="rounded-2xl border border-hairline bg-surface px-3 py-2">
              <dt className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">Hardness class</dt>
              <dd className="mt-1 text-lg font-extrabold capitalize text-ink" data-testid="hardness-class">
                {result.hardnessClass}
              </dd>
            </div>
            <div className="rounded-2xl border border-hairline bg-surface px-3 py-2">
              <dt className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">Oxygen saturation</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink" data-testid="oxygen-saturation">
                {result.oxygenSaturationPercent === null ? 'Not in table' : `${formatModelNumber(result.oxygenSaturationPercent, 0)} %`}
              </dd>
            </div>
          </dl>
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="quality-step"
            result={result.result}
            resultTestId="quality-result"
            limitNote={result.limitNote}
          />
        </div>
      </div>
    </section>
  );
}
